import { tradeAllocationApi } from 'apiConnector/trade-allocation';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { ofType } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import type { EpicWithThunk } from 'state/state.common';
import {
  tradeAllocationApplyRequestFailed,
  tradeAllocationApplyResponseReceived,
} from 'state/tradeAllocationForm/tradeAllocationForm.actions';
import type { WorkspaceTabRemoved } from 'state/workspaces/workspace.actions';

export function applyTradeAllocationEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    { selectors: { tradeAllocationSelectors }, frontLogger, oldHttpClient, getSgmeConfiguration },
  ) => {
    return action$.pipe(
      ofType('TRADE_ALLOCATION_APPLY_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const tabRemoved$ = action$.pipe(
          ofType('WORKSPACE_TAB_REMOVED'),
          filter((action: WorkspaceTabRemoved) => action.id === tabId),
        );

        const request = tradeAllocationSelectors.getApplyRequest(state, tabId)!;
        const tradeId = tradeAllocationSelectors.getTradeId(state, tabId)!;
        const tradeAllocationTimeoutMs = getSgmeConfiguration().tradeAllocationTimeoutMs;
        return tradeAllocationApi(oldHttpClient, tradeAllocationTimeoutMs)
          .apply(tradeId, request)
          .pipe(
            takeUntil(tabRemoved$),
            map(response => tradeAllocationApplyResponseReceived(tabId, response)),
            catchError((error: Error) => {
              frontLogger.error('An error occurred in applyTradeAllocationEpic {@FrontError}', error);
              const errorMessage = {
                message: getApiErrorMessage(error),
              };
              return of(tradeAllocationApplyRequestFailed(tabId, errorMessage, error));
            }),
          );
      }),
    );
  };
}

function getApiErrorMessage(error: Error) {
  if (apiErrorHelper.isHttpError(error)) {
    if (error.status === 403) {
      return 'tradeAllocation.error.request.forbidden';
    }

    if (error.status === 504) {
      return 'tradeAllocation.error.request.timeout';
    }

    if (error.message !== undefined) {
      return error.message;
    }
  }

  if (apiErrorHelper.isHttpTimeoutError(error)) {
    return 'tradeAllocation.error.request.timeout';
  }

  return 'tradeAllocation.error.request.technical';
}
